/* ===========================================================
   Didier's Patisserie — static site content
   Ported from the Claude design (data.jsx). Marketing copy and
   editorial imagery live here; product/range data lives in
   catalogue.ts and is fed from the catalogue (Trac-IT) in prod.
   =========================================================== */

/**
 * Base for editorial imagery. Served SAME-ORIGIN from the site's own /assets
 * (bundled in public/assets/{slides,promos,media}) so it works on any host —
 * the preview subdomain today and didiers.co.uk after cutover — with no
 * dependency on the old external site.
 */
export const IMG = '/assets';

/** Resolve an absolute image url from a relative media path. */
export const mediaUrl = (path: string): string => `${IMG}/${path}`;

export interface PromoBlock {
  title: string;
  img: string;
  body: string[];
}

export interface AboutBlock {
  heading?: string;
  img?: string;
  body: string[];
}

export interface CsrItem {
  heading: string;
  body: string;
}

export interface SiteContent {
  slides: string[];
  branding?: {
    headerLogo?: string;
    cddLogo?: string;
  };
  home: {
    tagline: string;
    promos: PromoBlock[];
  };
  about: {
    hero: string;
    intro: string[];
    blocks: AboutBlock[];
    founders: string;
  };
  innovation: {
    eyebrow: string;
    title: string;
    lede: string;
    sub: string;
    body: string[];
    ctaHeading: string;
  };
  csr: {
    eyebrow: string;
    title: string;
    lede: string;
    pillarsTitle?: string;
    items: CsrItem[];
  };
  contact: {
    email: string;
    phone: string;
    phoneHref: string;
    member: string;
    instagram: string;
    instagramUrl: string;
  };
  legal?: {
    privacy: { title: string; body: string[] };
    cookie: { title: string; body: string[] };
  };
}

export const CONTENT: SiteContent = {
  slides: [
    `${IMG}/slides/1-337-1290x575.jpg`,
    `${IMG}/slides/2-338-1290x575.jpg`,
    `${IMG}/slides/3-339-1290x575.jpg`,
  ],

  branding: {
    headerLogo: '/assets/didiers-logo.svg',
    cddLogo: '/assets/cdd-logo.png',
  },

  home: {
    tagline: "London's finest patisserie made to royal standards.",
    promos: [
      {
        title: 'What do we do?',
        img: `${IMG}/promos/1-image1.jpg`,
        body: [
          'We create bespoke, innovative and authentic premium patisserie & desserts and deliver them nationwide.',
          'Our commitment is to offer products of the highest standard in the way ingredients are sourced, the way products are developed, produced, presented and delivered to you.',
        ],
      },
      {
        title: 'Who are our clients?',
        img: `${IMG}/promos/2-image2.jpg`,
        body: [
          'We work with a large list of loyal clients ranging across the entire food services industry and beyond.',
          'Clients include international airlines, well-known restaurant chains and a highly reputable distribution platform.',
          'We are also covering prestigious events such as Wimbledon, Glyndebourne Opera and more.',
        ],
      },
      {
        title: 'Who are we?',
        img: `${IMG}/promos/3-image3.jpg`,
        body: [
          'We are a team of professionals truly passionate about patisserie.',
          'It is that same passion that has brought us fantastic recognition over 25 years.',
        ],
      },
      {
        title: 'What can we do for you?',
        img: `${IMG}/promos/4-image4.jpg`,
        body: [
          'We welcome the opportunity to discuss how we can create premium patisserie for you.',
          'Our friendly and professional approach makes the entire process enjoyable and you can rest assured we will surpass your expectations.',
          'We look forward to hearing from you today.',
        ],
      },
    ],
  },

  about: {
    hero: `${IMG}/media/1-about-didiers.jpg`,
    intro: [
      'From childhood dream to chef, Didier first discovered patisserie as a young boy helping his father to deliver milk to local patisseries. Drawn by the aroma of hot croissants fresh from the oven, he was fascinated by the Chefs patissiers with their inherited skills and techniques.',
    ],
    blocks: [
      {
        img: `${IMG}/promos/5-didier-history.jpg`,
        body: [
          'From then on, he was determined to discover for himself how to make these marvellous creations. So began his lifelong passion for fine patisserie.',
          'Since then, Didier has had the good fortune to work with some of the masters including Albert Roux and Lucien Peltier, each one inspiring him even further to search for fresh ideas and the finest ingredients.',
        ],
      },
      {
        heading: 'From Paris to London',
        img: `${IMG}/promos/6-french-patisserie.jpg`,
        body: [
          'In 1994, head hunted by Albert Roux, Didier crossed the channel to join the house of Albert Roux. Seduced by London, Didier and his wife Laure started their own business venture.',
          'Didier\u2019s Patisserie Ltd. was founded in June 1995 with the mission to become the No.1 patisserie manufacturer in the UK.',
        ],
      },
      {
        heading: '2018 brand identity refresh',
        body: [
          'We started 2018 with a brand identity refresh. Retaining the essence and elegance of the brand the colours were selected to reach an even larger market. The introduction of the shapes above the name allows for icons when needed. These shapes can be placed with or without the main identity to bring little touches to all we do.',
          'We look forward to seeing the brand continue to grow.',
        ],
      },
    ],
    founders: 'Didier Merveilleux & Laure Merveilleux',
  },

  innovation: {
    eyebrow: 'Innovation',
    title: 'Bespoke Patisserie',
    lede: 'Where your ideas meet 25 years of pastry craft.',
    sub: 'Tell us what you imagine — a signature dessert, a seasonal range, a showpiece for an event — and our development team will bring it to life to the highest standard.',
    body: [
      'Innovation sits at the heart of everything we do. From concept and recipe development to tasting, refinement and production, we partner closely with our clients to create premium patisserie that is unmistakably theirs.',
      'Every bespoke project is treated with the same attention to detail as our signature range — authentic techniques, the finest ingredients, and an obsession with presentation.',
    ],
    ctaHeading: "Let's create something together.",
  },

  csr: {
    eyebrow: 'CSR / Sustainability',
    title: 'Doing things the right way',
    lede: 'Premium standards extend beyond the plate.',
    pillarsTitle: 'We have four pillars to our sustainability programme',
    items: [
      { heading: 'Responsible sourcing', body: 'We source ingredients to the highest standards, working with trusted suppliers who share our values on quality and provenance.' },
      { heading: 'Reducing waste', body: 'From recipe development to production and packaging, we work continually to reduce waste across our operation.' },
      { heading: 'Our people', body: 'A team of professionals truly passionate about patisserie — supported, trained and respected.' },
      { heading: 'Community', body: 'We support our local community and industry through partnerships, events and education.' },
    ],
  },

  contact: {
    email: 'didiers@compagniedesdesserts.com',
    phone: '0208 153 1005',
    phoneHref: 'tel:+442081531005',
    member: 'La Compagnie Des Desserts',
    instagram: '@didiers_patisserie',
    instagramUrl: 'https://www.instagram.com/didiers_patisserie/',
  },

  legal: {
    privacy: {
      title: 'Privacy Policy',
      body: ['Privacy policy content to be provided.'],
    },
    cookie: {
      title: 'Cookie Policy',
      body: ['Cookie policy content to be provided.'],
    },
  },
};
